import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Ship,
  MapPin,
  AlertCircle,
  CheckCircle,
  Eye,
  Activity,
  FileText,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import {
  Button,
  Alert,
  Badge,
  MetricCard,
  Skeleton,
  ResponsiveTable,
  Tooltip,
  Container,
  Stack,
} from '@/components/ui';
import type { ResponsiveTableColumn } from '@/components/ui';
import { MainLayout } from '@/layouts/MainLayout';
import { PositionListService } from '@/services/positionList.service';
import {
  formatDateDDMMYYYY,
  formatDWTValue,
  formatSpeed,
  formatPoolPoints,
  parseRtfToPlainText,
} from '@/utils/positionListUtils';
import type {
  Position,
  VesselItinerary,
  VesselRestriction,
  VesselVetting,
  VesselObservation,
} from '@/types/positionList.types';

export function PositionDetailsPage() {
  const { area, vesselId } = useParams<{ area: string; vesselId: string }>();
  const navigate = useNavigate();

  // State
  const [position, setPosition] = useState<Position | null>(null);
  const [itineraries, setItineraries] = useState<VesselItinerary[]>([]);
  const [restrictions, setRestrictions] = useState<VesselRestriction[]>([]);
  const [vettings, setVettings] = useState<VesselVetting[]>([]);
  const [observations, setObservations] = useState<VesselObservation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState({
    itinerary: true,
    restrictions: true,
    vettings: true,
    observations: false,
  });

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    const fetchData = async () => {
      if (!area || !vesselId) {
        navigate('/position-list');
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const vesselIdNumber = parseInt(vesselId, 10);

        // Fetch all data in parallel
        const [positionData, itinerariesData, restrictionsData, vettingsData, observationsData] =
          await Promise.allSettled([
            PositionListService.getPositionDetails(area, vesselIdNumber),
            PositionListService.getVesselItineraries(vesselIdNumber),
            PositionListService.getVesselRestrictions(vesselIdNumber),
            PositionListService.getVesselVettings(vesselIdNumber),
            PositionListService.getVesselObservations(vesselIdNumber),
          ]);

        // Handle position data (required)
        if (positionData.status === 'fulfilled') {
          setPosition(positionData.value);
        } else {
          throw new Error('Failed to load vessel details');
        }

        // Handle optional data
        if (itinerariesData.status === 'fulfilled') {
          setItineraries(itinerariesData.value);
        }

        if (restrictionsData.status === 'fulfilled') {
          setRestrictions(restrictionsData.value);
        }

        if (vettingsData.status === 'fulfilled') {
          setVettings(vettingsData.value);
        }

        if (observationsData.status === 'fulfilled') {
          setObservations(observationsData.value);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load vessel details');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [area, vesselId, navigate]);

  const handleGoBack = () => {
    navigate('/position-list');
  };

  if (loading) {
    return (
      <MainLayout>
        <Container className="pb-8">
          <Stack direction="vertical" spacing={6} className="min-w-0">
            {/* Header Skeleton */}
            <div className="flex items-start gap-4">
              <Skeleton className="mt-3 h-10 w-10 rounded-full" />
              <Skeleton className="h-16 w-16 rounded-2xl" />
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-64" />
                  <Skeleton className="h-6 w-12 rounded-full" />
                </div>
                <Skeleton className="mt-2 h-5 w-48" />
              </div>
            </div>

            {/* Metrics Skeleton */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 lg:gap-6">
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="rounded-lg border border-gray-200 bg-white p-6 dark:border-dark-700 dark:bg-dark-800"
                >
                  <Skeleton className="mb-3 h-3 w-24" />
                  <Skeleton className="h-8 w-20" />
                </div>
              ))}
            </div>

            {/* Sections Skeleton */}
            <div className="space-y-4">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800"
                >
                  <div className="flex items-center justify-between px-6 py-4">
                    <div className="flex items-center gap-3">
                      <Skeleton className="h-5 w-5 rounded" />
                      <Skeleton className="h-6 w-32" />
                      <Skeleton className="h-4 w-16" />
                    </div>
                    <Skeleton className="h-5 w-5" />
                  </div>
                  <div className="border-t border-gray-200 dark:border-dark-700">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                        <thead className="bg-gray-50 dark:bg-dark-800">
                          <tr>
                            {Array.from({ length: 4 }).map((_, j) => (
                              <th key={j} className="px-6 py-3">
                                <Skeleton className="h-4 w-20" />
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                          {Array.from({ length: 3 }).map((_, j) => (
                            <tr key={j}>
                              {Array.from({ length: 4 }).map((_, k) => (
                                <td key={k} className="px-6 py-4">
                                  <Skeleton className="h-4 w-full" />
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </Stack>
        </Container>
      </MainLayout>
    );
  }

  if (error || !position) {
    return (
      <MainLayout>
        <Container className="pb-8">
          <Stack direction="vertical" spacing={6} className="min-w-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleGoBack}
              leftIcon={<ArrowLeft className="h-4 w-4" />}
              className="w-fit"
            >
              Back to Position List
            </Button>

            <Alert variant="error">
              <div>
                <p className="font-medium">Failed to load vessel details</p>
                <p className="text-sm opacity-90">
                  {error || 'The requested vessel could not be found.'}
                </p>
              </div>
            </Alert>
          </Stack>
        </Container>
      </MainLayout>
    );
  }

  // Helper functions
  const getStatusBadge = (status: string) => {
    if (status.toLowerCase() === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
          <CheckCircle className="h-3 w-3" />
          Completed
        </span>
      );
    }
    if (status.toLowerCase() === 'pending') {
      return <Badge variant="warning">{status}</Badge>;
    }
    if (status.toLowerCase() === 'cancelled') {
      return <Badge variant="error">{status}</Badge>;
    }
    return status ? <Badge variant="default">{status}</Badge> : null;
  };

  // Group itineraries by charterer (like Angular implementation)
  const getGroupedItineraries = () => {
    if (itineraries.length === 0) return [];

    // Sort by charterer to group them together
    const sorted = [...itineraries].sort((a, b) => {
      const chartererA = a.charterer || '';
      const chartererB = b.charterer || '';
      return chartererA.localeCompare(chartererB);
    });

    // Group by charterer
    const groups: { charterer: string; items: VesselItinerary[] }[] = [];
    let currentGroup: { charterer: string; items: VesselItinerary[] } | null = null;

    for (const item of sorted) {
      const charterer = item.charterer || 'Unknown';
      if (!currentGroup || currentGroup.charterer !== charterer) {
        currentGroup = { charterer, items: [item] };
        groups.push(currentGroup);
      } else {
        currentGroup.items.push(item);
      }
    }

    return groups;
  };

  const groupedItineraries = getGroupedItineraries();

  // Table column definitions
  const restrictionsColumns: ResponsiveTableColumn<VesselRestriction>[] = [
    { key: 'portName', header: 'Port Name', primary: true },
    { key: 'restriction', header: 'Restriction' },
    { key: 'reason', header: 'Reason' },
    { key: 'remark', header: 'Remarks', render: (value) => value || '-' },
  ];

  const vettingsColumns: ResponsiveTableColumn<VesselVetting>[] = [
    { key: 'inspectionCompany', header: 'Company', primary: true },
    { key: 'type', header: 'Type' },
    {
      key: 'inspectionDate',
      header: 'Date',
      render: (value) => formatDateDDMMYYYY(value) || '-',
    },
    {
      key: 'expiryDate',
      header: 'Expiry',
      render: (value) => formatDateDDMMYYYY(value) || '-',
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => getStatusBadge(value || ''),
    },
    { key: 'activity', header: 'Activity', render: (value) => value || '-' },
    {
      key: 'inSire',
      header: 'In SIRE',
      render: (value) => (value ? 'YES' : 'NO'),
    },
    { key: 'port', header: 'Port', render: (value) => value || '-' },
    { key: 'updatedBy', header: 'Source', render: (value) => value || '-' },
  ];

  // Collapsible Section Header Component
  const SectionHeader = ({
    icon: Icon,
    title,
    count,
    isExpanded,
    onClick,
    iconColor,
  }: {
    icon: React.ElementType;
    title: string;
    count?: number;
    isExpanded: boolean;
    onClick: () => void;
    iconColor: string;
  }) => (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full min-w-0 items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-dark-700 sm:px-6 sm:py-4"
    >
      <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
        <Icon className={`h-5 w-5 shrink-0 ${iconColor}`} />
        <h2 className="min-w-0 text-lg font-bold text-gray-900 dark:text-white sm:text-xl">
          {title}
        </h2>
        {count !== undefined && (
          <span className="shrink-0 text-sm text-gray-500 dark:text-gray-400">({count})</span>
        )}
      </div>
      {isExpanded ? (
        <ChevronUp className="h-5 w-5 shrink-0 text-gray-400" />
      ) : (
        <ChevronDown className="h-5 w-5 shrink-0 text-gray-400" />
      )}
    </button>
  );

  return (
    <MainLayout>
      <Container className="pb-8">
        <Stack direction="vertical" spacing={6} className="min-w-0">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-4">
            <div className="flex items-start gap-3 sm:gap-4">
              <Tooltip content="Back to Position List" position="right">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleGoBack}
                  className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full p-0 hover:bg-gray-100 dark:hover:bg-dark-700 sm:mt-3"
                  aria-label="Back to Position List"
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
              </Tooltip>

              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg sm:h-16 sm:w-16">
                <Ship className="h-7 w-7 text-white sm:h-8 sm:w-8" />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:gap-3">
                <h1 className="break-words text-2xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-3xl lg:text-4xl">
                  {position.vesselName}
                </h1>
                <span className="w-fit rounded-full bg-primary-100 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary-700 dark:bg-primary-900/40 dark:text-primary-300 sm:text-sm">
                  {area?.toUpperCase()}
                </span>
              </div>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 sm:text-base">
                Vessel overview • Built {position.built}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-5 lg:gap-6">
            <MetricCard
              label="Deadweight"
              value={position.dwt ? formatDWTValue(position.dwt) : 'N/A'}
              size="lg"
              className="hover:border-primary-400 dark:hover:border-primary-500"
            />
            <MetricCard
              label="Pool Points"
              value={position.poolpointTIFPR ? formatPoolPoints(position.poolpointTIFPR) : 'N/A'}
              size="lg"
              className="hover:border-primary-400 dark:hover:border-primary-500"
            />
            <MetricCard
              label="Ballast Speed"
              value={
                position.lastBallastSpeed ? `${formatSpeed(position.lastBallastSpeed)} mts` : 'N/A'
              }
              size="lg"
              className="hover:border-primary-400 dark:hover:border-primary-500"
            />
            <MetricCard
              label="Laden Speed"
              value={
                position.lastLadenSpeed ? `${formatSpeed(position.lastLadenSpeed)} mts` : 'N/A'
              }
              size="lg"
              className="hover:border-primary-400 dark:hover:border-primary-500"
            />
            <MetricCard
              label="Next Dry Dock"
              value={formatDateDDMMYYYY(position.nextDD) || 'N/A'}
              size="lg"
              className="hover:border-primary-400 dark:hover:border-primary-500"
            />
          </div>

          {/* Status Badges */}
          {(position.bitOnSubs === 1 ||
            position.onRestrictionList === 1 ||
            position.onObservationList === 1 ||
            position.shownToMarket === 1) && (
            <div className="flex flex-wrap gap-2">
              {position.bitOnSubs === 1 && (
                <Badge variant="warning" size="lg">
                  On Subs
                </Badge>
              )}
              {position.onRestrictionList === 1 && (
                <Badge variant="error" size="lg">
                  Has Restrictions
                </Badge>
              )}
              {position.onObservationList === 1 && (
                <Badge variant="warning" size="lg">
                  On Observation
                </Badge>
              )}
              {position.shownToMarket === 1 && (
                <Badge variant="success" size="lg">
                  Shown to Market
                </Badge>
              )}
            </div>
          )}

          {/* Comments Section */}
          {position.comment && (
            <div className="rounded-lg border-l-4 border-primary-500 bg-primary-50 p-4 dark:bg-primary-900/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary-600 dark:text-primary-400" />
                <div>
                  <h3 className="mb-1 font-semibold text-primary-900 dark:text-primary-300">
                    Comments
                  </h3>
                  <p className="whitespace-pre-wrap text-primary-800 dark:text-primary-200">
                    {position.comment}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Trading Restrictions Alert */}
          {position.tradingRestrictions && (
            <div className="rounded-lg border-l-4 border-red-500 bg-red-50 p-4 dark:bg-red-900/20">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-600 dark:text-red-400" />
                <div>
                  <h3 className="mb-1 font-semibold text-red-900 dark:text-red-300">
                    Trading Restrictions
                  </h3>
                  <p className="text-red-800 dark:text-red-200">{position.tradingRestrictions}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {/* Itinerary Section */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
              <SectionHeader
                icon={MapPin}
                title="Itinerary"
                count={itineraries.length}
                isExpanded={expandedSections.itinerary}
                onClick={() => toggleSection('itinerary')}
                iconColor="text-primary-600 dark:text-primary-400"
              />
              {expandedSections.itinerary && (
                <div className="border-t border-gray-200 dark:border-dark-700">
                  {itineraries.length > 0 ? (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200 dark:divide-dark-700">
                        <thead className="bg-gray-50 dark:bg-dark-800">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                              Purpose
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                              Port
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                              Arrival
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                              Departure
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                              Speed
                            </th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-600 dark:text-gray-300">
                              Note
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white dark:divide-dark-700 dark:bg-dark-900">
                          {groupedItineraries.map((group) => (
                            <React.Fragment key={group.charterer}>
                              {/* Charterer Group Header */}
                              <tr className="bg-gray-100 dark:bg-dark-700">
                                <td
                                  colSpan={6}
                                  className="px-4 py-2 text-sm font-bold text-gray-900 dark:text-white"
                                >
                                  {group.charterer}
                                </td>
                              </tr>
                              {/* Itinerary Items */}
                              {group.items.map((item, idx) => (
                                <tr
                                  key={`${group.charterer}-${idx}`}
                                  className="hover:bg-gray-50 dark:hover:bg-dark-800"
                                >
                                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-900 dark:text-white">
                                    {item.purpose || '-'}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                    {item.port || '-'}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                    {formatDateDDMMYYYY(item.arrivalTime) || '-'}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                    {formatDateDDMMYYYY(item.departureTime) || '-'}
                                  </td>
                                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                    {item.speed ? `${formatSpeed(item.speed)} mts` : '-'}
                                  </td>
                                  <td className="px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                                    <span className="whitespace-pre-wrap break-words">
                                      {parseRtfToPlainText(item.note) || '-'}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No available data
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Port Restrictions Section */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
              <SectionHeader
                icon={AlertCircle}
                title="Port Restrictions"
                count={restrictions.length}
                isExpanded={expandedSections.restrictions}
                onClick={() => toggleSection('restrictions')}
                iconColor="text-orange-600 dark:text-orange-400"
              />
              {expandedSections.restrictions && (
                <div className="border-t border-gray-200 dark:border-dark-700">
                  {restrictions.length > 0 ? (
                    <ResponsiveTable
                      data={restrictions}
                      columns={restrictionsColumns}
                      keyExtractor={(_, index) => index}
                      striped
                      hover
                      showViewToggle={false}
                      tableWrapperClassName="rounded-none rounded-b-lg border-0"
                    />
                  ) : (
                    <div className="flex items-center justify-center gap-2 px-6 py-8 text-green-600 dark:text-green-400">
                      <CheckCircle className="h-5 w-5" />
                      <span>No restrictions found for this vessel</span>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Vettings Section */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
              <SectionHeader
                icon={Activity}
                title="Vettings"
                count={vettings.length}
                isExpanded={expandedSections.vettings}
                onClick={() => toggleSection('vettings')}
                iconColor="text-purple-600 dark:text-purple-400"
              />
              {expandedSections.vettings && (
                <div className="border-t border-gray-200 dark:border-dark-700">
                  {vettings.length > 0 ? (
                    <ResponsiveTable
                      data={vettings}
                      columns={vettingsColumns}
                      keyExtractor={(row) => row.id}
                      striped
                      hover
                      showViewToggle={false}
                      tableWrapperClassName="rounded-none rounded-b-lg border-0"
                    />
                  ) : (
                    <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No available data
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Observations Section */}
            <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm dark:border-dark-700 dark:bg-dark-800">
              <SectionHeader
                icon={FileText}
                title="Observations"
                count={observations.length}
                isExpanded={expandedSections.observations}
                onClick={() => toggleSection('observations')}
                iconColor="text-gray-500 dark:text-gray-400"
              />
              {expandedSections.observations && (
                <div className="border-t border-gray-200 dark:border-dark-700">
                  {observations.length > 0 ? (
                    <div className="divide-y divide-gray-200 dark:divide-dark-700">
                      {observations.map((observation) => (
                        <div
                          key={observation.id}
                          className="flex items-start gap-3 p-4 transition-colors hover:bg-gray-50 dark:hover:bg-dark-700"
                        >
                          <Eye className="mt-1 h-5 w-5 flex-shrink-0 text-amber-500" />
                          <div className="flex-1">
                            <div className="mb-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {formatDateDDMMYYYY(observation.createdDate)} by{' '}
                                {observation.createdBy}
                              </span>
                              <Badge
                                variant={observation.resolved ? 'success' : 'warning'}
                                className="w-fit"
                              >
                                {observation.resolved ? 'Resolved' : 'Active'}
                              </Badge>
                            </div>
                            <p className="text-gray-700 dark:text-gray-300">
                              {observation.observation}
                            </p>
                            {observation.resolved && observation.resolvedDate && (
                              <div className="mt-2 text-sm text-green-600 dark:text-green-400">
                                Resolved on {formatDateDDMMYYYY(observation.resolvedDate)}
                                {observation.resolvedBy && ` by ${observation.resolvedBy}`}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="px-6 py-8 text-center text-gray-500 dark:text-gray-400">
                      No available data
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </Stack>
      </Container>
    </MainLayout>
  );
}
